import ExcelJS from "exceljs";
import moment from "moment";
import { ExportPdfColor } from "../../../../../root/theme/exportColor";

function isDarkColor(color: string): boolean {
  let r = 0,
    g = 0,
    b = 0;

  if (color.startsWith("rgb")) {
    const match = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    } else {
      return false;
    }
  } else if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return false;
    }
  } else {
    return false;
  }

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 128;
}

const getColor = (value: number, list: any) => {
  const startColor = { r: 222, g: 255, b: 235 }; // #DEFFEB
  const endColor = { r: 2, g: 105, b: 87 }; // #026957
  const index = list.indexOf(value);

  const maxIndex = list.length - 1;
  const ratio = maxIndex === 0 ? 0 : index / maxIndex;
  const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
  const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
  const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);

  const color = Math.round(value) === 0 ? "#444850" : `rgb(${r}, ${g}, ${b})`; // Make it gradually more green

  return color;
};

const parseRGB = (input: any) => {
  const regex = /rgb\(\s*(-?\d+)\s*,\s*(-?\d+)\s*,\s*(-?\d+)\s*\)/i;
  const match = input.match(regex);
  if (match) {
    const [, r, g, b] = match;
    return {
      r: Number(r),
      g: Number(g),
      b: Number(b),
    };
  } else {
    throw new Error("Invalid RGB format");
  }
};

const extractValue = (input: any) => {
  return input === "-" ? 0 : parseFloat(input.toString().split(" ")[0]);
};

const isDaySelectedNumber = (inputDay: any, selectedDaysList: any) => {
  if (selectedDaysList.includes("monday") && inputDay === 0) {
    return true;
  } else if (selectedDaysList.includes("tuesday") && inputDay === 1) {
    return true;
  } else if (selectedDaysList.includes("wednesday") && inputDay === 2) {
    return true;
  } else if (selectedDaysList.includes("thursday") && inputDay === 3) {
    return true;
  } else if (selectedDaysList.includes("friday") && inputDay === 4) {
    return true;
  } else if (selectedDaysList.includes("saturday") && inputDay === 5) {
    return true;
  } else if (selectedDaysList.includes("sunday") && inputDay === 6) {
    return true;
  } else {
    return false;
  }
};

export const handleDownloadExcelWeekly = async (
  columns: any[],
  nodes: any[],
  filterDetails: any,
  value: any,
  selected: any,
  valueTotal: any,
  selectedDaysList: any,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Hourly Sales Report");
  const filteredColumns = columns.filter((col: string, i: number) => {
    const isNotHide = isDaySelectedNumber(i, selectedDaysList);

    return isNotHide;
  });
  /* Worksheet add headers */
  worksheet.columns = filteredColumns;

  /* Find the last character in the excel */
  const len = filteredColumns.length + 64;
  let char = String.fromCharCode(len);

  /* Add a title */
  worksheet.getCell("A1").value = "Hourly Sales report";

  worksheet.mergeCells(`B1:F1`);

  const splitFilter = filterDetails.split("*");
  let filterOptions = "";

  /* Change of sub-title according to the number of locations.
    If the location is one, then that location is displayed, 
    if more than one location, the number of locations is displayed. */
  if (splitFilter[0].split(",").length === 1) {
    filterOptions =
      "Hourly Sales for " +
      splitFilter[0].split(",")[0] +
      " " +
      "Generated for the period of " +
      splitFilter[1];
  } else {
    filterOptions =
      "Hourly Sales for " +
      splitFilter[0].split(",").length +
      " Locations " +
      "Generated for the period of " +
      splitFilter[1];
  }

  /* Add sub-title using filter options. */
  worksheet.getCell("B1").value = filterOptions;
  // Align the value in B1 to the left
  worksheet.getCell("B1").alignment = { horizontal: "left" };
  worksheet.addRow({});
  const tableHeader = ["Hour", ...filteredColumns];
  worksheet.addRow(tableHeader);

  const isDayIncluded = (dayIndex: any) => {
    const daysOfWeek = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const dayName = daysOfWeek[dayIndex];
    return selectedDaysList.includes(dayName);
  };

  nodes.forEach((row, rowIndex) => {
    const rowData = [row.hour];
    row.data.forEach((cell: any, index: any) => {
      if (isDayIncluded(index)) {
        if (selected === "all") {
          rowData.push(
            `${Math.round(cell.count)} (${
              cell.total === 0 ? "-" : parseFloat(cell.total).toFixed(2)
            })`,
          );
        } else if (selected === "count") {
          rowData.push(Math.round(cell.count));
        } else {
          rowData.push(
            cell.total === 0 ? "-" : parseFloat(cell.total).toFixed(2),
          );
        }
      }
    });

    const insertedRow = worksheet.addRow(rowData);

    insertedRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (rowIndex % 2 === 0) {
        // Even index -> Odd row color in PDF
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: ExportPdfColor.OddRowColorLight.replace("#", "") },
        };
        cell.font = { color: { argb: "000000" } };
      } else {
        // Odd index -> Even row color
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: ExportPdfColor.EvenRowColorLight.replace("#", "") },
        };
        cell.font = { color: { argb: "000000" } };
      }

      if (colNumber !== 1) {
        const colorString = getColor(
          extractValue(cell.value),
          selected === "total" ? valueTotal : value,
        );

        if (colorString.startsWith("rgb")) {
          const { r, g, b } = parseRGB(colorString);
          const hex = ((1 << 24) + (r << 16) + (g << 8) + b)
            .toString(16)
            .slice(1)
            .toUpperCase();

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: hex },
          };
          cell.font = {
            color: { argb: isDarkColor(colorString) ? "FFFFFFFF" : "00000000" },
          }; // black text
        }
      }

      cell.alignment = { horizontal: "center" };
    });
  });

  const headerRow = worksheet.getRow(3);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: ExportPdfColor.EvenRowColorLight.replace("#", "") },
    };
    cell.font = { color: { argb: "000000" } };
    cell.alignment = { horizontal: "center" };
  });

  worksheet.getRow(1).height = 30; // Set height of the first row

  // OR loop through all rows and set a uniform height
  worksheet.eachRow((row) => {
    row.getCell(1).alignment = { horizontal: "left" };
    row.getCell(filteredColumns.length + 1).alignment = { horizontal: "right" };
    row.height = 30; // Set custom height for all rows
  });

  // Auto width for all columns
  worksheet.columns.forEach((col: any) => {
    let maxLength = 20;
    col.eachCell({ includeEmpty: true }, (cell: any) => {
      maxLength = Math.max(maxLength, cell.value?.toString().length || 0);
    });
    col.width = Math.min(maxLength + 2, 20); // Ensure columns do not exceed a width of 30
  });
  // Save Excel file
  const today = moment().format("MMM_Do_YY").toString();
  const fileName = `Hourly_Sales_Report_${today}_${Math.floor(
    100000 + Math.random() * 900000,
  )}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
